import { useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, Bitcoin, DollarSign, ArrowRight, Check, ExternalLink, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const amounts = [10, 25, 50, 100, 250, 500];

const RechargePage = () => {
  const { token, user } = useAuth();
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);

  const handleRecharge = async () => {
    const finalAmount = customAmount ? parseFloat(customAmount) : amount;
    
    if (finalAmount < 10) {
      toast.error("Minimum recharge amount is $10");
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/payment/create`, 
        { amount: finalAmount, currency: "USD" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setPaymentLink(res.data.pay_link);
        toast.success("Payment created! Redirecting...");
        
        // Open payment link in new tab
        window.open(res.data.pay_link, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="recharge-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Recharge Account</h1>
        <p className="text-slate-500 mt-1">Add credit to your account using cryptocurrency</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recharge Form */}
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
          >
            {/* Current Balance */}
            <div className="bg-slate-900 text-white rounded-xl p-6 mb-6">
              <p className="text-slate-400 text-sm">Current Balance</p>
              <p className="text-4xl font-bold mt-1">${parseFloat(user?.credit || 0).toFixed(2)}</p>
            </div>

            {/* Amount Selection */}
            <div className="mb-6">
              <Label className="mb-3 block">Select Amount</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {amounts.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => {
                      setAmount(amt);
                      setCustomAmount("");
                    }}
                    className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                      amount === amt && !customAmount
                        ? 'bg-brand-500 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`amount-${amt}`}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div className="mb-6">
              <Label htmlFor="custom-amount">Or Enter Custom Amount</Label>
              <div className="relative mt-2">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="custom-amount"
                  type="number"
                  min="10"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount (min $10)"
                  className="pl-10"
                  data-testid="custom-amount-input"
                />
              </div>
            </div>

            {/* Total */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Amount to Pay</span>
                <span className="text-2xl font-bold text-slate-900">
                  ${customAmount ? parseFloat(customAmount).toFixed(2) : amount.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Pay Button */}
            <Button
              onClick={handleRecharge}
              disabled={loading}
              className="w-full bg-brand-500 hover:bg-brand-600 text-white py-6 text-lg font-semibold rounded-xl"
              data-testid="pay-btn"
            >
              {loading ? (
                "Processing..."
              ) : (
                <>
                  <Bitcoin className="w-5 h-5 mr-2" />
                  Pay with Crypto
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {paymentLink && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-green-700 font-medium mb-2">Payment Created!</p>
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-green-600 hover:text-green-700"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Click here if payment page didn't open
                </a>
              </div>
            )}
          </motion.div>
        </div>

        {/* Info Panel */}
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Bitcoin className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Crypto Payment</h3>
            </div>
            <p className="text-slate-600 text-sm mb-4">
              We accept multiple cryptocurrencies through OxaPay:
            </p>
            <ul className="space-y-2 text-sm">
              {["Bitcoin (BTC)", "Ethereum (ETH)", "USDT (TRC20/ERC20)", "Litecoin (LTC)", "And more..."].map((coin, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-600">
                  <Check className="w-4 h-4 text-green-500" />
                  {coin}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Secure & Fast</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Instant credit after confirmation
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Secure payment gateway
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                No additional fees
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                24/7 support via Telegram
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RechargePage;
